import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import OptimizedImage from './ui/OptimizedImage';
import OptimizedVideo from './ui/OptimizedVideo';


export interface MediaAsset {
  url: string;
  type: 'image' | 'video';
  id: string;
  poster?: string;
}

interface GalleryProps {
  assets: MediaAsset[];
  onAssetClick?: (index: number) => void;
  className?: string;
  editable?: boolean;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  onDelete?: (index: number) => void;
  onSetPrimary?: (index: number, type: 'photo' | 'video') => void;
  showControls?: boolean;
  gridCols?: number;
  separateMediaTypes?: boolean;
  initialLoadCount?: number; // Number of items to show initially
  loadMoreCount?: number; // Number of items to load when "Load More" is clicked
}

const Gallery: React.FC<GalleryProps> = ({
  assets,
  onAssetClick,
  className = '',
  editable = false,
  onReorder,
  onDelete,
  onSetPrimary,
  showControls = false,
  gridCols = 6,
  separateMediaTypes = false,
  initialLoadCount = 5,
  loadMoreCount = 5
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupIndex, setPopupIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(initialLoadCount);
  
  // Touch/swipe state for popup navigation
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (!editable) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!editable) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    if (!editable || draggedIndex === null) return;
    e.preventDefault();

    if (draggedIndex !== dropIndex && onReorder) {
      onReorder(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleAssetClick = (index: number) => {
    setPopupIndex(index);
    setShowPopup(true);
    // Also call the external onAssetClick if provided
    onAssetClick?.(index);
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const nextPopupImage = () => {
    setPopupIndex((prev) => (prev + 1) % assets.length);
  };

  const prevPopupImage = () => {
    setPopupIndex((prev) => (prev - 1 + assets.length) % assets.length);
  };

  const isVideoUrl = (url: string) => {
    return url.toLowerCase().includes('.mp4') || url.toLowerCase().includes('.webm') || url.toLowerCase().includes('.mov');
  };

  // Swipe detection
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && assets.length > 1) {
      nextPopupImage();
    }
    if (isRightSwipe && assets.length > 1) {
      prevPopupImage();
    }
  };

  const renderMediaItem = (asset: MediaAsset, originalIndex: number, displayIndex: number) => (
    <div
      key={`${asset.id || asset.url}-${originalIndex}`}
      className={`relative group aspect-square rounded-lg overflow-hidden bg-dark-700 hover:opacity-80 transition-all ${editable ? 'cursor-move' : 'cursor-pointer'
        } ${draggedIndex === originalIndex ? 'scale-95 opacity-50' : 'hover:scale-105'} transform`}
      draggable={editable}
      onDragStart={(e) => handleDragStart(e, originalIndex)}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, originalIndex)}
      onDragEnd={handleDragEnd}
      onClick={() => {
        // Allow click if not dragging
        if (draggedIndex === null) {
          handleAssetClick(originalIndex);
        }
      }}
      title={asset.type === 'video' ? `Video: ${asset.id}` : `Image: ${asset.id}`}
    >
      {asset.type === 'video' || isVideoUrl(asset.url) ? (
        <OptimizedVideo
          src={asset.url}
          poster={asset.poster}
          className="w-full h-full"
          priority={displayIndex < 4}
          muted={true}
          loop={false}
          controls={false}
          autoPlay={false}
        />
      ) : (
        <OptimizedImage
          src={asset.url}
          alt={`Media ${displayIndex + 1}`}
          className="w-full h-full"
          placeholder="🖼️"
          priority={displayIndex < 4}
        />
      )}

      {/* Primary indicator - moved to bottom-left */}
      {(asset as any).isPrimary && (
        <div className="absolute bottom-1 left-1 bg-purple-600 text-white text-xs px-2 py-1 rounded z-20 font-medium">
          PRIMARY {((asset as any).primaryType || '').toUpperCase()}
        </div>
      )}

      {editable && showControls && (
        <>
          {/* Drag indicator - center */}
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
            <span className="text-white text-xs bg-black/50 px-2 py-1 rounded">🔄 Drag</span>
          </div>

          {/* Delete button - top-right corner */}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(originalIndex);
              }}
              className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors opacity-0 group-hover:opacity-100 flex items-center justify-center text-sm z-30 shadow-lg"
              title="Remove asset"
            >
              ×
            </button>
          )}

          {/* Set Primary buttons - top-left, only show if not already primary */}
          {onSetPrimary && !(asset as any).isPrimary && (
            <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1 z-20">
              {(asset.type === 'image' || !isVideoUrl(asset.url)) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetPrimary(originalIndex, 'photo');
                  }}
                  className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors shadow-lg flex items-center gap-1"
                  title="Set as primary photo"
                >
                  📷 Primary
                </button>
              )}
              {(asset.type === 'video' || isVideoUrl(asset.url)) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Primary video button clicked for index:', originalIndex);
                    onSetPrimary(originalIndex, 'video');
                  }}
                  className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors shadow-lg flex items-center gap-1"
                  title="Set as primary video"
                >
                  🎥 Primary
                </button>
              )}
            </div>
          )}

          {/* Index number - bottom-right, only show if not primary (to avoid overlap) */}
          {!(asset as any).isPrimary && (
            <div className="absolute bottom-1 right-1 bg-dark-800/80 text-white text-xs px-2 py-1 rounded">
              #{displayIndex + 1}
            </div>
          )}
        </>
      )}

      {editable && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity truncate">
          {asset.url.split('/').pop()}
        </div>
      )}
    </div>
  );

  if (assets.length === 0) {
    return (
      <div className={`text-center py-8 text-dark-400 ${className}`}>
        <div className="text-4xl mb-2">📷</div>
        <p>No media assets available</p>
      </div>
    );
  }

  // Separate images and videos if requested
  if (separateMediaTypes) {
    const images = assets.filter(asset => asset.type === 'image' || !isVideoUrl(asset.url));
    const videos = assets.filter(asset => asset.type === 'video' || isVideoUrl(asset.url));

    // Apply pagination to both images and videos
    const visibleImages = images.slice(0, visibleCount);
    const visibleVideos = videos.slice(0, visibleCount);

    return (
      <div className={className}>
        {/* Images Section */}
        {(visibleImages.length > 0 || images.length > 0) && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              📷 Images ({images.length})
            </h3>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
              {visibleImages.map((asset, index) => {
                const originalIndex = assets.findIndex(a => a.id === asset.id);
                return renderMediaItem(asset, originalIndex, index);
              })}

              {/* Load More button in grid for images */}
              {visibleImages.length < images.length && (
                <div
                  key="load-more-images"
                  className="relative group aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-accent-purple/20 to-accent-pink/20 hover:from-accent-purple/30 hover:to-accent-pink/30 cursor-pointer transition-all duration-300 hover:scale-105 flex items-center justify-center border-2 border-dashed border-accent-purple/50 hover:border-accent-purple"
                  onClick={() => setVisibleCount(prev => prev + loadMoreCount)}
                >
                  <div className="text-center text-white">
                    <div className="text-2xl mb-2">+</div>
                    <div className="text-xs font-medium">Load More</div>
                    <div className="text-xs opacity-75">({images.length - visibleImages.length} more)</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Videos Section */}
        {(visibleVideos.length > 0 || videos.length > 0) && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              🎥 Videos ({videos.length})
            </h3>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
              {visibleVideos.map((asset, index) => {
                const originalIndex = assets.findIndex(a => a.id === asset.id);
                return renderMediaItem(asset, originalIndex, index);
              })}

              {/* Load More button in grid for videos */}
              {visibleVideos.length < videos.length && (
                <div
                  key="load-more-videos"
                  className="relative group aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-accent-purple/20 to-accent-pink/20 hover:from-accent-purple/30 hover:to-accent-pink/30 cursor-pointer transition-all duration-300 hover:scale-105 flex items-center justify-center border-2 border-dashed border-accent-purple/50 hover:border-accent-purple"
                  onClick={() => setVisibleCount(prev => prev + loadMoreCount)}
                >
                  <div className="text-center text-white">
                    <div className="text-2xl mb-2">+</div>
                    <div className="text-xs font-medium">Load More</div>
                    <div className="text-xs opacity-75">({videos.length - visibleVideos.length} more)</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}



        {/* FULLSCREEN POPUP */}
        {showPopup && assets.length > 0 && createPortal(
          <div className="fixed inset-0 bg-black z-[9999] flex items-center justify-center">
            <div 
              className="relative w-full h-full"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <button
                onClick={closePopup}
                className="absolute top-4 right-4 w-10 h-10 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-black/90 z-20"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {assets.length > 1 && (
                <>
                  <button
                    onClick={prevPopupImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-black/90 z-20"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextPopupImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-black/90 z-20"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              <div className="w-full h-full flex items-center justify-center">
                {assets[popupIndex]?.type === 'video' || isVideoUrl(assets[popupIndex]?.url) ? (
                  <OptimizedVideo
                    src={assets[popupIndex].url}
                    poster={assets[popupIndex].poster}
                    className="w-full h-full"
                    autoPlay={true}
                    controls={true}
                    muted={true}
                    loop={true}
                    priority={true}
                    objectFit="contain"
                  />
                ) : (
                  <img
                    src={assets[popupIndex]?.url}
                    alt={`Gallery item ${popupIndex + 1}`}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>

              {assets.length > 1 && (
                <div className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-2 rounded-full z-20">
                  {popupIndex + 1} / {assets.length}
                </div>
              )}


            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Original combined view with pagination
  const visibleAssets = assets.slice(0, visibleCount);
  const hasMore = visibleAssets.length < assets.length;

  return (
    <>
      <div className={`grid gap-2 ${className}`} style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
        {visibleAssets.map((asset, index) => renderMediaItem(asset, index, index))}

        {/* Load More button in grid for combined view */}
        {hasMore && (
          <div
            key="load-more-combined"
            className="relative group aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-accent-purple/20 to-accent-pink/20 hover:from-accent-purple/30 hover:to-accent-pink/30 cursor-pointer transition-all duration-300 hover:scale-105 flex items-center justify-center border-2 border-dashed border-accent-purple/50 hover:border-accent-purple"
            onClick={() => setVisibleCount(prev => prev + loadMoreCount)}
          >
            <div className="text-center text-white">
              <div className="text-2xl mb-2">+</div>
              <div className="text-xs font-medium">Load More</div>
              <div className="text-xs opacity-75">({assets.length - visibleAssets.length} more)</div>
            </div>
          </div>
        )}
      </div>

      {/* FULLSCREEN POPUP MODAL */}
      {showPopup && assets.length > 0 && createPortal(
        <div className="fixed inset-0 bg-black z-[9999] flex items-center justify-center">
          <div 
            className="relative w-full h-full flex items-center justify-center bg-black overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Close button - NO BACKDROP BLUR, NO TRANSITIONS */}
            <button
              onClick={closePopup}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-black/90 z-30"
            >
              <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Navigation arrows - NO BACKDROP BLUR, NO TRANSITIONS */}
            {assets.length > 1 && (
              <>
                <button
                  onClick={prevPopupImage}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-black/90 z-30"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={nextPopupImage}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-black/70 rounded-full flex items-center justify-center text-white hover:bg-black/90 z-30"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* SMOOTH VIDEO WITH FULLSCREEN SIZING */}
            <div className="absolute inset-0 flex items-center justify-center">
              {assets[popupIndex]?.type === 'video' || isVideoUrl(assets[popupIndex]?.url) ? (
                /* EXACT LOGINMODAL VIDEO - ZERO CHANGES */
                <OptimizedVideo
                  src={assets[popupIndex].url}
                  poster={assets[popupIndex].poster}
                  className="w-full h-full"
                  autoPlay={true}
                  muted={true}
                  loop={true}
                  controls={true}
                  priority={true}
                  objectFit="contain"
                  onLoad={() => {
                    console.log('🎬 Gallery popup OptimizedVideo loaded');
                  }}
                  onError={() => {
                    console.error('🚫 Gallery popup OptimizedVideo failed');
                  }}
                />
              ) : (
                <img
                  src={assets[popupIndex]?.url}
                  alt={`Gallery item ${popupIndex + 1}`}
                  className="w-full h-full object-contain"
                />
              )}
            </div>

            {/* Counter - NO BACKDROP BLUR */}
            {assets.length > 1 && (
              <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 bg-black/70 text-white text-xs sm:text-sm px-2 py-1 sm:px-3 sm:py-2 rounded-full z-30">
                {popupIndex + 1} / {assets.length}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Gallery;